import { action, observable } from 'mobx';
import type { Answer, Question, Relation } from '../../types';
import type { Survey } from '../Survey';
import { sanitizeUserComment } from './sanitizeUserComment';
import { ValidationError } from './error-types';

export class QuestionModel {
  @observable accessor userComment: string = ''; // коммент юзера к вопросу или ответ на вопрос типа Text
  @observable.ref accessor selectedAnswers: Answer[] = [];
  @observable accessor disabled: boolean = false; // отключаем все поля ввода, кнопки и т.п.
  @observable accessor validationErrors: {
    emptyUserCommentError?: boolean;
    noSelectedAnswersError?: boolean;
  } = {};

  /**
   * @param suitableRelation Переход, который привел юзера к данному вопросу. Отсутствует для самого первого вопроса анкеты.
   */
  constructor(
    protected survey: Survey,
    public question: Question,
    protected suitableRelation?: Relation | null
  ) {
    /**
     * Если включена рандомизация (question.is_random), то бэк перемешивает ответы в списке, но не меняет answer_number.
     * Необходимо подогнать answer_number под позицию ответа в списке, чтобы дальнейшая сортировка не сбросила рандомизацию.
     */
    if (this.question.is_random) {
      question.form_answers.forEach((answer, i) => (answer.answer_number = i + 1));
    }
    question.form_answers.sort((a1, a2) => a1.answer_number - a2.answer_number);
  }

  @action
  set_selectedAnswers(selectedAnswers: Answer[]) {
    this.selectedAnswers = selectedAnswers;
    this.validationErrors.noSelectedAnswersError = false;
  }

  @action
  set_userComment(userComment: string) {
    this.userComment = sanitizeUserComment(userComment);
    this.validationErrors.emptyUserCommentError = false;
  }

  @action
  set_disabled(disabled: boolean) {
    this.disabled = disabled;
  }

  /**
   * Если валидация не пройдена, бросит исключение.
   */
  @action
  validateAnswers() {
    // Если юзер не заполнил обязательный коммент.
    this.validationErrors.emptyUserCommentError = this.question.is_comment && this.question.is_comment_required && !this.userComment;

    // Если юзер не выбрал ни одного варианта ответа на обязательный вопрос, у которого есть варианты ответов.
    this.validationErrors.noSelectedAnswersError = this.question.is_required && !this.selectedAnswers.length;

    this.throwIfInvalid();
  }

  @action
  throwIfInvalid() {
    if (Object.values(this.validationErrors).some((value) => value)) {
      throw new ValidationError();
    }
  }
}
